import React, {
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import {
    Box,
    Button,
    Alert,
    AlertTitle,
    Stack,
    Typography
} from "@mui/material";

import GridOnIcon from "@mui/icons-material/GridOn";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ClearIcon from "@mui/icons-material/Clear";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import Header from "./Header";
import CollapsiblePanel from "./CollapsiblePanel";
import History from "./History";
import DataInput from "./DataInput";
import { getInitialState } from "./DefaultParameters";

const createEmptyPredictRows = (count = 5) =>
    Array.from(
        { length: count },
        () => ({
            sampleId: "",
            response1: "",
            response2: ""
        })
    );

const loadStoredCalibrationResponse = () => {
    try {
        const raw = localStorage.getItem(
            "microslideCalibrationResponse"
        );

        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const parseResponses = (value) => {

    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return [];
    }

    if (Array.isArray(value)) {

        return value
            .filter(v =>
                v !== null &&
                v !== undefined &&
                String(v).trim() !== ""
            )
            .map(v => Number(v))
            .filter(v =>
                Number.isFinite(v)
            );
    }

    return String(value)
        .split(/[,\s]+/)
        .map(v => v.trim())
        .filter(v => v !== "")
        .map(v => Number(v))
        .filter(v =>
            Number.isFinite(v)
        );
};


/*
 * Convert calibration rows into the same
 * graph format used by Calibration Graph.
 *
 * X = Response
 * Y = SAV
 */
const buildCalibrationData = (rows) => {

    const resp1 = [];
    const resp2 = [];

    if (!Array.isArray(rows)) {
        return {
            resp1,
            resp2
        };
    }

    rows.forEach(row => {

        if (!row) {
            return;
        }

        if (
            row.sav === null ||
            row.sav === undefined ||
            String(row.sav).trim() === ""
        ) {
            return;
        }

        const sav = Number(row.sav);

        if (!Number.isFinite(sav)) {
            return;
        }

        /*
         * RESP1
         */
        parseResponses(row.resp1)
            .forEach(response => {

                resp1.push([
                    response,
                    sav
                ]);

            });

        /*
         * RESP2
         */
        parseResponses(row.resp2)
            .forEach(response => {

                resp2.push([
                    response,
                    sav
                ]);

            });

    });

    return {
        resp1,
        resp2
    };
};


/*
 * Linear regression
 */
const calculateTrendline = (data) => {

    if (
        !Array.isArray(data) ||
        data.length < 2
    ) {
        return {
            line: [],
            slope: 0,
            intercept: 0,
            r2: 0
        };
    }

    const n = data.length;

    const sumX = data.reduce(
        (sum, point) =>
            sum + point[0],
        0
    );

    const sumY = data.reduce(
        (sum, point) =>
            sum + point[1],
        0
    );

    const sumXY = data.reduce(
        (sum, point) =>
            sum + point[0] * point[1],
        0
    );

    const sumX2 = data.reduce(
        (sum, point) =>
            sum + point[0] * point[0],
        0
    );

    const denominator =
        n * sumX2 -
        sumX * sumX;

    if (denominator === 0) {

        return {
            line: [],
            slope: 0,
            intercept: 0,
            r2: 0
        };
    }

    const slope =
        (
            n * sumXY -
            sumX * sumY
        ) /
        denominator;

    const intercept =
        (
            sumY -
            slope * sumX
        ) /
        n;

    const meanY =
        sumY / n;

    const ssTot =
        data.reduce(
            (sum, point) =>
                sum +
                Math.pow(
                    point[1] - meanY,
                    2
                ),
            0
        );

    const ssRes =
        data.reduce(
            (sum, point) => {

                const predicted =
                    slope * point[0] +
                    intercept;

                return sum +
                    Math.pow(
                        point[1] -
                        predicted,
                        2
                    );
            },
            0
        );

    const r2 =
        ssTot === 0
            ? 1
            : 1 -
            ssRes / ssTot;

    const xValues =
        data.map(point => point[0]);

    const minX =
        Math.min(...xValues);

    const maxX =
        Math.max(...xValues);

    const midX =
        (minX + maxX) / 2;

    const line = [
        [
            minX,
            slope * minX + intercept
        ],
        [
            midX,
            slope * midX + intercept
        ],
        [
            maxX,
            slope * maxX + intercept
        ]
    ];

    return {
        line,
        slope,
        intercept,
        r2
    };
};


export default function PredictionLayout({

    /*
     * IMPORTANT:
     *
     * These come from the parent MicroSlide component.
     * No navigation is required.
     */
    calibrationRows = [],
    calibrationResult = null

}) {

    const [resultsCollapsed, setResultsCollapsed] =
        useState(true);

    const [dataCollapsed, setDataCollapsed] =
        useState(false);

    const [chartCollapsed, setChartCollapsed] =
        useState(false);

    const [rows, setRows] =
        useState(
            createEmptyPredictRows(5)
        );

    const [parameters, setParameters] =
        useState(
            getInitialState()
        );

    const chartContainerRef =
        useRef(null);


    /*
     * Calibration response
     *
     * First use prop from parent.
     * localStorage is only fallback for refresh.
     */
    const [activeCalibration, setActiveCalibration] =
        useState(
            calibrationResult ||
            loadStoredCalibrationResponse()
        );


    /*
     * Prediction history
     */
    const [resultDataMap, setResultDataMap] =
        useState(() => {

            const saved =
                sessionStorage.getItem(
                    "microslideHistory_predict"
                );

            return saved
                ? JSON.parse(saved)
                : {};
        });


    /*
     * Whenever Calibration tab produces
     * a new calibration result, update it here.
     */
    useEffect(() => {

        if (
            calibrationResult &&
            Object.keys(calibrationResult).length > 0
        ) {

            setActiveCalibration(
                calibrationResult
            );

            localStorage.setItem(
                "microslideCalibrationResponse",
                JSON.stringify(
                    calibrationResult
                )
            );
        }

    }, [calibrationResult]);


    /*
     * Put calibration coefficients
     * into prediction parameters.
     */
    useEffect(() => {

        if (!activeCalibration) {
            return;
        }

        setParameters(prev => ({

            ...prev,

            fCalInterceptA0:
                String(
                    activeCalibration
                        .fCalInterceptA0 ??
                    prev.fCalInterceptA0 ??
                    0
                ),

            fCalSlopeA1:
                String(
                    activeCalibration
                        .fCalSlopeA1 ??
                    prev.fCalSlopeA1 ??
                    1
                ),

            fCalCurvatureA2:
                String(
                    activeCalibration
                        .fCalCurvatureA2 ??
                    prev.fCalCurvatureA2 ??
                    0
                ),

            fCalBlankCorrectionFactorA3:
                String(
                    activeCalibration
                        .fCalBlankCorrectionFactorA3 ??
                    prev.fCalBlankCorrectionFactorA3 ??
                    0
                )
        }));

    }, [activeCalibration]);


    /*
     * =========================================================
     * CALIBRATION GRAPH DATA
     * =========================================================
     */
    const calibrationData = useMemo(
        () =>
            buildCalibrationData(
                calibrationRows
            ),
        [calibrationRows]
    );


    /*
     * Existing calibration trendlines
     */
    const resp1Trend = useMemo(
        () =>
            calculateTrendline(
                calibrationData.resp1
            ),
        [calibrationData.resp1]
    );


    const resp2Trend = useMemo(
        () =>
            calculateTrendline(
                calibrationData.resp2
            ),
        [calibrationData.resp2]
    );


    /*
     * =========================================================
     * LATEST PREDICTION DATA
     * =========================================================
     */
    const latestPredictions = useMemo(() => {

        const runs =
            Object.values(
                resultDataMap || {}
            );

        if (runs.length === 0) {
            return [];
        }

        const latest =
            runs[runs.length - 1];

        const predictions =
            latest?.result?.predictions;

        return Array.isArray(
            predictions
        )
            ? predictions
            : [];

    }, [resultDataMap]);


    /*
     * =========================================================
     * PREDICTION GRAPH DATA
     *
     * Prediction points are converted to:
     *
     * X = Response
     * Y = Concentration
     *
     * They are displayed on the SAME graph.
     * =========================================================
     */
    const predictionResp1Data = useMemo(() => {

        return latestPredictions
            .map(p => {

                const response =
                    Number(p.response1);

                const concentration =
                    Number(p.concentration);

                if (
                    !Number.isFinite(response) ||
                    !Number.isFinite(
                        concentration
                    )
                ) {
                    return null;
                }

                return {
                    x: response,
                    y: concentration,
                    sampleId:
                        p.sampleId || "",
                    response1:
                        p.response1,
                    response2:
                        p.response2,
                    concentration:
                        p.concentration,
                    conditionCode:
                        p.conditionCode
                };

            })
            .filter(Boolean);

    }, [latestPredictions]);


    const predictionResp2Data = useMemo(() => {

        return latestPredictions
            .map(p => {

                const response =
                    Number(p.response2);

                const concentration =
                    Number(p.concentration);

                if (
                    !Number.isFinite(response) ||
                    !Number.isFinite(
                        concentration
                    )
                ) {
                    return null;
                }

                return {
                    x: response,
                    y: concentration,
                    sampleId:
                        p.sampleId || "",
                    response1:
                        p.response1,
                    response2:
                        p.response2,
                    concentration:
                        p.concentration,
                    conditionCode:
                        p.conditionCode
                };

            })
            .filter(Boolean);

    }, [latestPredictions]);


    /*
     * =========================================================
     * COMBINED GRAPH
     * =========================================================
     *
     * Existing Calibration axes:
     *
     * X = Response
     * Y = SAV
     *
     * Calibration and Prediction are plotted together.
     */
    const chartOptions = useMemo(() => {

        return {

            chart: {
                type: "scatter",
                height: 450,
                zoomType: "xy"
            },


            title: {
                text:
                    "Calibration + Prediction Curve"
            },


            xAxis: {

                title: {
                    text: "Response"
                },

                gridLineWidth: 1

            },


            yAxis: {

                title: {
                    text: "SAV"
                },

                gridLineWidth: 1

            },


            legend: {
                enabled: true
            },


            tooltip: {

                useHTML: true,

                formatter: function () {

                    /*
                     * Calibration point
                     */
                    if (
                        this.series.userOptions
                            .isCalibration
                    ) {

                        return `
                            <b>${this.series.name}</b><br/>
                            Response: ${this.x}<br/>
                            SAV: ${this.y}
                        `;
                    }


                    /*
                     * Prediction point
                     */
                    const p =
                        this.point.options ||
                        {};

                    return `
                        <b>${this.series.name}</b><br/>
                        Sample: ${p.sampleId || "-"
                        }<br/>
                        Response: ${this.x
                        }<br/>
                        Concentration: ${this.y
                        }${p.conditionCode
                            ? `<br/>Condition: ${p.conditionCode}`
                            : ""
                        }
                    `;
                }

            },


            plotOptions: {

                scatter: {

                    marker: {
                        radius: 5,
                        symbol: "circle"
                    }

                }

            },


            series: [

                /*
                 * =================================================
                 * CALIBRATION RESP1
                 * =================================================
                 */
                {
                    name: "Calibration Resp1",

                    type: "scatter",

                    data:
                        calibrationData.resp1,

                    isCalibration: true,

                    marker: {
                        radius: 5,
                        symbol: "circle"
                    }
                },


                /*
                 * =================================================
                 * CALIBRATION RESP1 TRENDLINE
                 * =================================================
                 */
                {
                    name:
                        "Calibration Resp1 Trendline",

                    type: "line",

                    data:
                        resp1Trend.line,

                    isCalibration: true,

                    marker: {
                        enabled: false
                    },

                    enableMouseTracking: false,

                    dashStyle: "Dash",

                    lineWidth: 2
                },


                /*
                 * =================================================
                 * CALIBRATION RESP2
                 * =================================================
                 */
                {
                    name: "Calibration Resp2",

                    type: "scatter",

                    data:
                        calibrationData.resp2,

                    isCalibration: true,

                    marker: {
                        radius: 5,
                        symbol: "circle"
                    }
                },


                /*
                 * =================================================
                 * CALIBRATION RESP2 TRENDLINE
                 * =================================================
                 */
                {
                    name:
                        "Calibration Resp2 Trendline",

                    type: "line",

                    data:
                        resp2Trend.line,

                    isCalibration: true,

                    marker: {
                        enabled: false
                    },

                    enableMouseTracking: false,

                    dashStyle: "Dash",

                    lineWidth: 2
                },


                /*
                 * =================================================
                 * PREDICTION RESP1
                 * =================================================
                 */
                {
                    name:
                        "Prediction Resp1",

                    type: "scatter",

                    data:
                        predictionResp1Data,

                    marker: {
                        radius: 7,
                        symbol: "diamond"
                    }
                },


                /*
                 * =================================================
                 * PREDICTION RESP2
                 * =================================================
                 */
                {
                    name:
                        "Prediction Resp2",

                    type: "scatter",

                    data:
                        predictionResp2Data,

                    marker: {
                        radius: 7,
                        symbol: "diamond"
                    }
                }

            ],


            credits: {
                enabled: false
            }

        };

    }, [
        calibrationData,
        resp1Trend,
        resp2Trend,
        predictionResp1Data,
        predictionResp2Data
    ]);


    /*
     * =========================================================
     * CLEAR CALIBRATION
     * =========================================================
     */
    const clearCalibration = () => {

        setActiveCalibration(null);

        localStorage.removeItem(
            "microslideCalibrationResponse"
        );

        setParameters(prev => ({

            ...prev,

            fCalInterceptA0: "0",

            fCalSlopeA1: "1",

            fCalCurvatureA2: "0",

            fCalBlankCorrectionFactorA3: "0"

        }));
    };


    /*
     * =========================================================
     * EXPORT EXCEL
     * =========================================================
     */
    const exportToExcel = () => {

        if (
            calibrationData.resp1.length === 0 &&
            calibrationData.resp2.length === 0 &&
            latestPredictions.length === 0
        ) {

            alert(
                "No calibration or prediction data available."
            );

            return;
        }


        const wb =
            XLSX.utils.book_new();


        /*
         * Calibration data
         */
        const calibrationRowsForExcel = [];


        calibrationData.resp1.forEach(
            point => {

                calibrationRowsForExcel.push({

                    Type:
                        "Calibration Resp1",

                    Response:
                        point[0],

                    SAV:
                        point[1]

                });

            }
        );


        calibrationData.resp2.forEach(
            point => {

                calibrationRowsForExcel.push({

                    Type:
                        "Calibration Resp2",

                    Response:
                        point[0],

                    SAV:
                        point[1]

                });

            }
        );


        const calibrationSheet =
            XLSX.utils.json_to_sheet(
                calibrationRowsForExcel
            );


        XLSX.utils.book_append_sheet(
            wb,
            calibrationSheet,
            "Calibration"
        );


        /*
         * Prediction data
         */
        const predictionRowsForExcel =
            latestPredictions.map(
                (p, i) => ({

                    Sample:
                        p.sampleId ||
                        i + 1,

                    Response1:
                        p.response1 ??
                        "",

                    Response2:
                        p.response2 ??
                        "",

                    Concentration:
                        p.concentration ??
                        "",

                    ConditionCode:
                        p.conditionCode ??
                        "",

                    Error:
                        p.error ??
                        ""

                })
            );


        const predictionSheet =
            XLSX.utils.json_to_sheet(
                predictionRowsForExcel
            );


        XLSX.utils.book_append_sheet(
            wb,
            predictionSheet,
            "Prediction"
        );


        const stamp =
            new Date()
                .toISOString()
                .replace(/[:.]/g, "-")
                .slice(0, 19);


        XLSX.writeFile(
            wb,
            `MicroSlide_Calibration_Prediction_${stamp}.xlsx`
        );
    };


    /*
     * =========================================================
     * EXPORT PDF
     * =========================================================
     */
    const exportToPdf = async () => {

        if (
            calibrationData.resp1.length === 0 &&
            calibrationData.resp2.length === 0 &&
            latestPredictions.length === 0
        ) {

            alert(
                "No calibration or prediction data available."
            );

            return;
        }


        const doc =
            new jsPDF({
                orientation: "portrait",
                unit: "pt",
                format: "letter"
            });


        doc.setFontSize(14);

        doc.text(
            "MicroSlide Calibration + Prediction Report",
            40,
            40
        );


        doc.setFontSize(10);

        doc.text(
            `Generated: ${new Date().toLocaleString()}`,
            40,
            58
        );


        /*
         * Calibration coefficients
         */
        doc.text(
            "Calibration:",
            40,
            78
        );


        autoTable(
            doc,
            {

                startY: 84,

                head: [
                    [
                        "Coefficient",
                        "Value"
                    ]
                ],

                body: [

                    [
                        "A0",
                        String(
                            parameters
                                .fCalInterceptA0 ??
                            ""
                        )
                    ],

                    [
                        "A1",
                        String(
                            parameters
                                .fCalSlopeA1 ??
                            ""
                        )
                    ],

                    [
                        "A2",
                        String(
                            parameters
                                .fCalCurvatureA2 ??
                            ""
                        )
                    ],

                    [
                        "A3",
                        String(
                            parameters
                                .fCalBlankCorrectionFactorA3 ??
                            ""
                        )
                    ]

                ],

                styles: {
                    fontSize: 9
                },

                margin: {
                    left: 40,
                    right: 40
                }

            }
        );


        /*
         * Add combined graph
         */
        try {

            if (
                chartContainerRef.current
            ) {

                const html2canvas =
                    (
                        await import(
                            "html2canvas"
                        )
                    ).default;


                const canvas =
                    await html2canvas(
                        chartContainerRef.current,
                        {
                            backgroundColor:
                                "#ffffff",
                            scale: 2
                        }
                    );


                const imgData =
                    canvas.toDataURL(
                        "image/png"
                    );


                const y =
                    (
                        doc.lastAutoTable
                            ?.finalY ??
                        120
                    ) + 16;


                doc.addImage(
                    imgData,
                    "PNG",
                    40,
                    y,
                    520,
                    260
                );
            }

        } catch (err) {

            console.warn(
                "Chart export skipped:",
                err
            );
        }


        /*
         * Prediction table
         */
        if (
            latestPredictions.length > 0
        ) {

            doc.addPage();

            doc.setFontSize(12);

            doc.text(
                "Predictions",
                40,
                40
            );


            autoTable(
                doc,
                {

                    startY: 50,

                    head: [[
                        "Sample",
                        "Resp1",
                        "Resp2",
                        "Concentration",
                        "Cond",
                        "Error"
                    ]],

                    body:
                        latestPredictions.map(
                            (p, i) => [

                                p.sampleId ||
                                i + 1,

                                p.response1 ??
                                "",

                                p.response2 ??
                                "",

                                p.concentration ??
                                "",

                                p.conditionCode ??
                                "",

                                p.error ??
                                ""

                            ]
                        ),

                    styles: {
                        fontSize: 9
                    },

                    margin: {
                        left: 40,
                        right: 40
                    }

                }
            );
        }


        const stamp =
            new Date()
                .toISOString()
                .replace(/[:.]/g, "-")
                .slice(0, 19);


        doc.save(
            `MicroSlide_Calibration_Prediction_${stamp}.pdf`
        );
    };


    const hasCalibration =
        !!activeCalibration;


    return (

        <div>

            <Header
                mode="predict"
                rows={rows}
                parameters={parameters}
                setParameters={setParameters}
                setResultDataMap={
                    setResultDataMap
                }
                onRun={() =>
                    setResultsCollapsed(false)
                }
            />


            <Box
                display="flex"
                flexDirection="column"
                padding={2}
            >

                {hasCalibration && (

                    <Alert
                        severity="success"
                        sx={{
                            mb: 2
                        }}
                        action={

                            <Button
                                size="small"
                                color="inherit"
                                startIcon={
                                    <ClearIcon />
                                }
                                onClick={
                                    clearCalibration
                                }
                            >
                                Clear
                            </Button>

                        }
                    >

                        <AlertTitle>
                            Calibration active
                        </AlertTitle>


                        <Stack
                            direction="row"
                            spacing={2}
                            flexWrap="wrap"
                        >

                            <Typography
                                variant="body2"
                            >
                                A0 =
                                {
                                    parameters
                                        .fCalInterceptA0
                                }
                            </Typography>


                            <Typography
                                variant="body2"
                            >
                                A1 =
                                {
                                    parameters
                                        .fCalSlopeA1
                                }
                            </Typography>


                            <Typography
                                variant="body2"
                            >
                                A2 =
                                {
                                    parameters
                                        .fCalCurvatureA2
                                }
                            </Typography>


                            <Typography
                                variant="body2"
                            >
                                A3 =
                                {
                                    parameters
                                        .fCalBlankCorrectionFactorA3
                                }
                            </Typography>

                        </Stack>

                    </Alert>

                )}


                <CollapsiblePanel
                    title="Sample Data (one row per sample)"
                    collapsed={dataCollapsed}
                    onToggle={() =>
                        setDataCollapsed(
                            !dataCollapsed
                        )
                    }
                >

                    <DataInput
                        columns={[
                            {
                                key: "sampleId",
                                label: "Sample ID"
                            },
                            {
                                key: "response1",
                                label: "Response 1"
                            },
                            {
                                key: "response2",
                                label: "Response 2"
                            }
                        ]}
                        rows={rows}
                        setRows={setRows}
                        placeholder={
                            "SampleID\tResp1\tResp2\n" +
                            "1\t0.5\t0.25\n" +
                            "2\t0.7\t0.35\n..."
                        }
                    />

                </CollapsiblePanel>


                <CollapsiblePanel
                    title="Results"
                    collapsed={
                        resultsCollapsed
                    }
                    onToggle={() =>
                        setResultsCollapsed(
                            !resultsCollapsed
                        )
                    }
                >

                    <History
                        resultDataMap={
                            resultDataMap
                        }
                        resultFields={[
                            "Sample Count",
                            "First Concentration",
                            "Last Concentration"
                        ]}
                    />

                </CollapsiblePanel>


                <CollapsiblePanel
                    title="Calibration + Prediction Chart"
                    collapsed={
                        chartCollapsed
                    }
                    onToggle={() =>
                        setChartCollapsed(
                            !chartCollapsed
                        )
                    }
                    headerExtras={

                        <Stack
                            direction="row"
                            spacing={1}
                        >

                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={
                                    <GridOnIcon />
                                }
                                onClick={e => {

                                    e.stopPropagation();

                                    exportToExcel();

                                }}
                                disabled={
                                    calibrationData.resp1.length === 0 &&
                                    calibrationData.resp2.length === 0 &&
                                    latestPredictions.length === 0
                                }
                                sx={{
                                    color: "#ffffff",
                                    borderColor:
                                        "rgba(255,255,255,0.7)"
                                }}
                            >
                                Excel
                            </Button>


                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={
                                    <PictureAsPdfIcon />
                                }
                                onClick={e => {

                                    e.stopPropagation();

                                    exportToPdf();

                                }}
                                disabled={
                                    calibrationData.resp1.length === 0 &&
                                    calibrationData.resp2.length === 0 &&
                                    latestPredictions.length === 0
                                }
                                sx={{
                                    color: "#ffffff",
                                    borderColor:
                                        "rgba(255,255,255,0.7)"
                                }}
                            >
                                PDF
                            </Button>

                        </Stack>
                    }
                >

                    <Box
                        sx={{
                            mb: 2
                        }}
                    >

                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Calibration:
                            {" "}
                            {calibrationData.resp1.length}
                            {" "}Resp1 points,
                            {" "}
                            {calibrationData.resp2.length}
                            {" "}Resp2 points
                            {"  |  "}
                            Prediction:
                            {" "}
                            {latestPredictions.length}
                            {" "}samples
                        </Typography>

                    </Box>


                    {calibrationData.resp1.length === 0 &&
                        calibrationData.resp2.length === 0 &&
                        latestPredictions.length === 0 ? (

                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            No calibration or prediction
                            data available.
                        </Typography>

                    ) : (

                        <div
                            ref={chartContainerRef}
                        >

                            <HighchartsReact
                                highcharts={
                                    Highcharts
                                }
                                options={
                                    chartOptions
                                }
                            />

                        </div>

                    )}

                </CollapsiblePanel>

            </Box>

        </div>
    );
}